import { useEffect, useRef, useState } from "react";
import { useRouter } from "../lib/router";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { getAllDrafts, deleteDraft, exportAllDrafts, importDrafts, type Draft, type ExportData } from "../lib/drafts";
import { getAllSurveys, deleteSurvey, type Survey } from "../lib/surveys";
import { useTheme } from "../lib/use-theme";

type DictationItem =
  | { type: "sop"; data: Draft }
  | { type: "blank"; data: Draft }
  | { type: "survey"; data: Survey };

export default function Home() {
  const { auth, isLoading, logout } = useAuth();
  const { navigate } = useRouter();
  const { theme, toggle: toggleTheme } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importStatus, setImportStatus] = useState("");
  const [items, setItems] = useState<DictationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTypePicker, setShowTypePicker] = useState(false);

  useEffect(() => {
    if (!isLoading && !auth) {
      navigate("/login");
    }
  }, [auth, isLoading, navigate]);

  useEffect(() => {
    Promise.all([getAllDrafts(), getAllSurveys()]).then(([drafts, surveys]) => {
      const merged: DictationItem[] = [
        ...drafts.map((d) =>
          d.type === "blank"
            ? ({ type: "blank" as const, data: d })
            : ({ type: "sop" as const, data: d })
        ),
        ...surveys.map((s) => ({ type: "survey" as const, data: s })),
      ].sort((a, b) => b.data.updatedAt - a.data.updatedAt);
      setItems(merged);
      setLoading(false);
    });
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!auth) return null;

  const handleNewSOP = () => {
    setShowTypePicker(false);
    const id = `draft-${Date.now()}`;
    navigate(`/dictate/${id}`);
  };

  const handleNewBlank = () => {
    setShowTypePicker(false);
    const id = `blank-${Date.now()}`;
    navigate(`/dictate/${id}`);
  };

  const handleNewSurvey = () => {
    setShowTypePicker(false);
    const id = `survey-${Date.now()}`;
    navigate(`/survey/${id}`);
  };

  const getInitials = (name: string) => {
    if (!name) return "?";
    const cleaned = name.replace(/@.*:.*/, "").replace(/[._-]/g, " ").trim();
    const parts = cleaned.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return cleaned.substring(0, 2).toUpperCase();
  };

  const handleDelete = async (item: DictationItem) => {
    if (item.type === "sop" || item.type === "blank") {
      await deleteDraft(item.data.id);
    } else {
      await deleteSurvey(item.data.id);
    }
    setItems((prev) => prev.filter((i) => i.data.id !== item.data.id));
  };

  const handleExport = async () => {
    const data = await exportAllDrafts();
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sop-drafts-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportStatus("");
    try {
      const text = await file.text();
      const data: ExportData = JSON.parse(text);
      const { imported, skipped } = await importDrafts(data);
      setImportStatus(`Imported ${imported} draft${imported !== 1 ? "s" : ""}${skipped ? `, skipped ${skipped} (newer local)` : ""}`);
      window.location.reload();
    } catch (err) {
      setImportStatus(`Import failed: ${err instanceof Error ? err.message : "Invalid file"}`);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const statusColors: Record<string, string> = {
    draft: "",
    "in-progress": "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
    structured: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    published: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  };

  const typeColors: Record<string, string> = {
    sop: "bg-violet-500/10 text-violet-400 border-violet-500/20",
    blank: "bg-slate-500/10 text-slate-300 border-slate-500/20",
    survey: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
  };

  const typeLabels: Record<string, string> = {
    sop: "SOP",
    blank: "Blank",
    survey: "Survey",
  };

  const formatDate = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const getTitle = (item: DictationItem) => {
    if (item.type === "sop") return item.data.title || "Untitled SOP";
    if (item.type === "blank") return item.data.title || "Untitled Document";
    return item.data.title || "Untitled Survey";
  };

  const getSubtitle = (item: DictationItem) => {
    if (item.type === "survey") {
      const s = item.data as Survey;
      return `${s.templateName} \u00b7 ${s.answers.filter((a) => a.answer.trim()).length} answered \u00b7 ${formatDate(s.updatedAt)}`;
    }
    return formatDate(item.data.updatedAt);
  };

  const getStatus = (item: DictationItem) => {
    if (item.type === "sop" || item.type === "blank") return (item.data as Draft).status || "draft";
    return item.data.status;
  };

  const handleItemClick = (item: DictationItem) => {
    if (item.type === "sop" || item.type === "blank") navigate(`/dictate/${item.data.id}`);
    else navigate(`/survey/${item.data.id}`);
  };

  return (
    <div className="min-h-screen max-w-2xl mx-auto">
      {/* Reva-style header */}
      <header className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-border bg-card/60 backdrop-blur sticky top-0 z-10">
        <div className="w-10 h-10 rounded-full vox-gradient flex items-center justify-center flex-shrink-0 shadow-md">
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="22"/>
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg sm:text-xl font-semibold tracking-tight leading-tight">VoxDoc</h1>
          <p className="text-xs text-muted-foreground font-medium">Voice-first document capture</p>
        </div>
        <button
          type="button"
          onClick={toggleTheme}
          title="Toggle theme"
          className="w-9 h-9 rounded-full border border-border bg-secondary flex items-center justify-center hover:border-primary/50 transition-colors"
        >
          {theme === "dark" ? "\u2600\uFE0F" : "\uD83C\uDF19"}
        </button>
        <button
          type="button"
          onClick={() => navigate("/settings")}
          title="Settings"
          className="w-9 h-9 rounded-full border border-border bg-secondary flex items-center justify-center hover:border-primary/50 transition-colors"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        </button>
        <button
          type="button"
          onClick={logout}
          title={auth.userId}
          className="w-9 h-9 rounded-full border border-border bg-secondary flex items-center justify-center text-[11px] font-bold text-primary tracking-wider uppercase hover:border-primary/50 transition-colors"
        >
          {getInitials(auth.userId)}
        </button>
      </header>

      <div className="px-4 sm:px-6 pt-6">

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold tracking-tight">Documents</h2>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={handleExport}
              className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-secondary"
            >
              Export
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-secondary"
            >
              Import
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
          </div>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowTypePicker(!showTypePicker)}
            className="vox-pill-btn text-sm flex items-center gap-1.5"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New
          </button>
          {showTypePicker && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowTypePicker(false)}
              />
              <div
                className="absolute right-0 top-full mt-2 z-20 border border-border rounded-xl shadow-2xl py-1.5 min-w-[220px] overflow-hidden"
                style={{ backgroundColor: "var(--popover)" }}
              >
                <button
                  className="w-full text-left px-4 py-3 text-sm font-medium hover:bg-secondary transition-colors flex items-start gap-3"
                  onClick={handleNewSOP}
                >
                  <span className="mt-0.5 text-violet-400">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="9" y1="13" x2="15" y2="13"/>
                      <line x1="9" y1="17" x2="15" y2="17"/>
                    </svg>
                  </span>
                  <span className="flex-1">
                    SOP
                    <span className="block text-xs font-normal text-muted-foreground mt-0.5">Standard operating procedure with AI structuring</span>
                  </span>
                </button>
                <div className="border-t border-border" />
                <button
                  className="w-full text-left px-4 py-3 text-sm font-medium hover:bg-secondary transition-colors flex items-start gap-3"
                  onClick={handleNewSurvey}
                >
                  <span className="mt-0.5 text-cyan-400">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <path d="M9 11l3 3L22 4"/>
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                    </svg>
                  </span>
                  <span className="flex-1">
                    Survey
                    <span className="block text-xs font-normal text-muted-foreground mt-0.5">Low-voltage system site walkthrough</span>
                  </span>
                </button>
                <div className="border-t border-border" />
                <button
                  className="w-full text-left px-4 py-3 text-sm font-medium hover:bg-secondary transition-colors flex items-start gap-3"
                  onClick={handleNewBlank}
                >
                  <span className="mt-0.5 text-slate-300">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <rect x="4" y="3" width="16" height="18" rx="2"/>
                    </svg>
                  </span>
                  <span className="flex-1">
                    Blank Document
                    <span className="block text-xs font-normal text-muted-foreground mt-0.5">Free-form dictation, no AI structuring</span>
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {importStatus && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          importStatus.startsWith("Import failed")
            ? "bg-destructive/10 text-destructive border border-destructive/20"
            : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
        }`}>
          {importStatus}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-border bg-card/40">
          <div className="w-12 h-12 rounded-full vox-gradient mx-auto mb-3 flex items-center justify-center opacity-80">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="22"/>
            </svg>
          </div>
          <p className="text-foreground font-medium mb-1">No documents yet</p>
          <p className="text-sm text-muted-foreground">
            Tap <span className="font-medium text-foreground">+ New</span> to start capturing with your voice.
          </p>
        </div>
      ) : (
        <div className="space-y-2.5 pb-8">
          {items.map((item) => {
            const status = getStatus(item);
            const statusLabel = status === "in-progress" ? "In Progress" : status.charAt(0).toUpperCase() + status.slice(1);
            return (
              <div
                key={item.data.id}
                className="vox-card flex items-center justify-between p-4 cursor-pointer"
                onClick={() => handleItemClick(item)}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="font-medium truncate">{getTitle(item)}</p>
                    <Badge className={typeColors[item.type]}>
                      {typeLabels[item.type]}
                    </Badge>
                    {status !== "draft" && (
                      <Badge className={statusColors[status] || ""}>
                        {statusLabel}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {getSubtitle(item)}
                  </p>
                </div>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive h-8 w-8 ml-2 rounded-full flex items-center justify-center transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item);
                  }}
                  aria-label="Delete"
                >
                  &times;
                </button>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
